import { Hours, Minutes, Seconds } from "../../Helpers";
import { DeviceResponseType } from "../Devices/IDevice";
import { BatteryState, BinarySensorState, BlindPostionState, BlindRemoteButtonState, ChannelOnOffState, CurrentWindThresholdState, DoorBellState, FacadeRemoteButtonState, FloodSensorState, GatePositionState, HumiditySensorState, IDeviceStateData, LightBrightnessState, LightColorState, LightWarmthState, MeasuredBrightnessState, MeasuredEnergyState, MeasuredTemperatureState, MovementSensorState, PressureSensorState, ReedState, RemoteButtonState, WindSpeedState } from "../Devices/IDeviceState";

/**
 * Day of week, from monday to sunday
 */
export type DayOfWeekArgument = 1 | 2 | 3 | 4 | 5 | 6 | 7

export class DeviceStateArgument {
    private _comparisonParams: Map<DeviceStateComparisonParams, any> = new Map();
    private _checkDeviceState: any;

    /**
     * Field is set when the method SetCheckDeviceState<T extends IDeviceStateData> is called, 
     * type is set based on instancof state: T - so this object so this object must have a prototype.
     * 
     * If the object has no prototypes you need to set this field manually AFTER calling SetCheckDeviceState<T extends IDeviceStateData>(state: T)
     */
    Type: DeviceResponseType = DeviceResponseType.Unknown;
    DeviceGuid: string = "";

    ComparisonMethod: DeviceStateComparisonMethod = DeviceStateComparisonMethod.Default;

    GetCheckDeviceState<T extends IDeviceStateData>(): T {
        return this._checkDeviceState;
    }

    SetCheckDeviceState<T extends IDeviceStateData>(state: T) {
        this._checkDeviceState = state;

        if (state instanceof BatteryState)
            this.Type = DeviceResponseType.BatteryState;

        if (state instanceof BinarySensorState)
            this.Type = DeviceResponseType.BinarySensorState;

        if (state instanceof BlindPostionState)
            this.Type = DeviceResponseType.BlindPosition;

        if (state instanceof LightBrightnessState)
            this.Type = DeviceResponseType.LightBrightness;

        if (state instanceof MeasuredBrightnessState)
            this.Type = DeviceResponseType.MeasuredBrightness;

        if (state instanceof RemoteButtonState)
            this.Type = DeviceResponseType.RemoteButtonState;

        if (state instanceof ChannelOnOffState)
            this.Type = DeviceResponseType.ChannelOnOffState;

        if (state instanceof DoorBellState)
            this.Type = DeviceResponseType.DoorBellState;

        if (state instanceof MeasuredEnergyState)
            this.Type = DeviceResponseType.MeasuredEnergy;

        if (state instanceof FloodSensorState)
            this.Type = DeviceResponseType.FloodSensorState;

        if (state instanceof GatePositionState)
            this.Type = DeviceResponseType.GatePosition;

        if (state instanceof LightColorState)
            this.Type = DeviceResponseType.LightColor;

        if (state instanceof LightWarmthState)
            this.Type = DeviceResponseType.LightWarmth;

        if (state instanceof ReedState)
            this.Type = DeviceResponseType.ReedState;

        if (state instanceof MeasuredTemperatureState)
            this.Type = DeviceResponseType.MeasuredTemperature;

        if (state instanceof HumiditySensorState)
            this.Type = DeviceResponseType.HumiditySensorState;

        if (state instanceof BlindRemoteButtonState)
            this.Type = DeviceResponseType.BlindRemoteButtonState;

        if (state instanceof FacadeRemoteButtonState)
            this.Type = DeviceResponseType.FacadeRemoteButtonState;

        if (state instanceof MovementSensorState)
            this.Type = DeviceResponseType.MovementSensorState;

        if (state instanceof MeasuredEnergyState)
            this.Type = DeviceResponseType.MeasuredEnergy;

        if (state instanceof PressureSensorState)
            this.Type = DeviceResponseType.PressureSensorState;

        if (state instanceof WindSpeedState)
            this.Type = DeviceResponseType.WindSpeedState;

        if (state instanceof CurrentWindThresholdState)
            this.Type = DeviceResponseType.CurrentWindThreshold;
    }

    GetComparisonParam<E extends IDeviceStateComparisonParams>(comparisonParam: DeviceStateComparisonParams): E {
        return this._comparisonParams.get(comparisonParam);
    }

    GetComparisonParams(): Map<DeviceStateComparisonParams, IDeviceStateComparisonParams> {
        return this._comparisonParams;
    }

    SetComparisonParam<E extends IDeviceStateComparisonParams>(comparisonParam: DeviceStateComparisonParams, value: E) {
        this._comparisonParams.set(comparisonParam, value);
    }
}

export interface IDeviceStateComparisonParams {
    get Value(): any;
}

export class NumberParam implements IDeviceStateComparisonParams {
    private _value: number;

    constructor(val: number) {
        this._value = val;
    }

    get Value(): number {
        return this._value
    }

    set Value(val: number) {
        this._value = val;
    }
}

export class TimeSpanParam implements IDeviceStateComparisonParams {
    private _value: string = "00:00:00";

    constructor(rawValue: string);
    constructor(seconds: Seconds, minutes: Minutes, hours: Hours, days: number);

    constructor(...args: Array<any>) {
        if (args.length > 1)
            this.SetTimeSpan(args[0], args[1], args[2], args[3]);
        else
            this._value = args[0];
    }

    public SetTimeSpan(seconds: Seconds, minutes: Minutes, hours: Hours, days: number) {
        if (days <= 0)
            this._value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        else
            this._value = `${days}.${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    get Value(): string {
        return this._value
    }
}

export enum DeviceStateComparisonMethod {
    Default = 0,
    Delta = 1,
    StateRepeat = 2
}

export enum DeviceStateComparisonParams {
    DeltaValue = 0,
    DeltaCalculationTimeRange = 1,
    StateRepeatCount = 2,
    StateRepeatTimeRange = 3
}

export class AstronomicalClockArgument {
    Position: SunPosition = SunPosition.SunRise;
}

export class AstronomicalClockWithOffsetArgument {
    Position: SunPosition = SunPosition.SunRise;
    Offset: number = 0;
}

export enum SunPosition {
    SunRise = 1,
    SunSet = 2
}

export class TimeArgument {
    constructor(seconds: Seconds, minutes: Minutes, hours: Hours);
    constructor()

    constructor(seconds?: Seconds, minutes?: Minutes, hours?: Hours) {
        if (seconds && minutes && hours) {
            this.Hour = hours;
            this.Minute = minutes;
            this.Second = seconds;
        }
    }

    Hour: Hours = 0;
    Minute: Minutes = 0;
    Second: Seconds = 0;
}

export class SimpleTimeSpanArgument {
    constructor(seconds: Seconds, minutes: Minutes, hours: Hours, days: number);
    constructor()

    constructor(seconds?: Seconds, minutes?: Minutes, hours?: Hours, days?: number) {
        if (seconds && minutes && hours && days) {
            this.days = days;
            this.hours = hours;
            this.minutes = minutes;
            this.seconds = seconds;
        }
    }

    hours: Hours = 0;
    minutes: Minutes = 0;
    seconds: Seconds = 0;
    days: number = 0;
}

export class ConditionInfoArgument {
    DeviceGuid: string = "";
}